import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterSite?: string;
  canonicalUrl?: string;
  jsonLdSchema?: object | object[];
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly defaultTitle = 'Nexchat AI Agent | Smart Chatbot Automation for Businesses';
  private readonly defaultDescription = 'Automate your business conversations with Nexchat AI Agent. Manage leads, respond instantly, and increase conversions using AI-powered automation.';
  private readonly defaultKeywords = 'Nexchat AI, chatbot, chat automation, AI assistant, business chatbot, lead automation, customer support AI';
  private readonly defaultOgImage = 'https://ai.myshopyglam.com/assets/icon/download%20(2)-Photoroom.png';
  private readonly defaultBaseUrl = 'https://thenexchat.com';

  constructor(
    @Inject(DOCUMENT) private dom: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    private title: Title,
    private meta: Meta
  ) {}

  /**
   * Main method to dynamically update all SEO tags for a route.
   */
  public updateSeo(data: SeoData = {}): void {
    const rawTitle = data.title ? `${data.title} | Nexchat AI Agent` : this.defaultTitle;
    const titleText = data.title && data.title.includes('Nexchat AI Agent') ? data.title : rawTitle;
    const description = data.description || this.defaultDescription;
    const keywords = data.keywords || this.defaultKeywords;
    const author = data.author || 'Nexchat AI Agent Team';
    const robots = data.robots || 'index, follow';
    const ogImage = data.ogImage || this.defaultOgImage;
    const ogType = data.ogType || 'website';
    const twitterCard = data.twitterCard || 'summary_large_image';
    const twitterSite = data.twitterSite || '@nexchatai';

    const currentUrl: string =
      data.canonicalUrl ||
      (isPlatformBrowser(this.platformId) && this.dom?.location?.href
        ? this.dom.location.href
        : this.defaultBaseUrl) ||
      this.defaultBaseUrl;

    // 1. Page Title
    this.title.setTitle(titleText);

    // 2. Core Meta Tags
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });
    this.meta.updateTag({ name: 'author', content: author });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ name: 'theme-color', content: '#00cbeb80' });

    // 3. Open Graph (Facebook, LinkedIn, WhatsApp, etc.)
    this.meta.updateTag({ property: 'og:title', content: data.ogTitle || titleText });
    this.meta.updateTag({ property: 'og:description', content: data.ogDescription || description });
    this.meta.updateTag({ property: 'og:image', content: ogImage });
    this.meta.updateTag({ property: 'og:url', content: data.ogUrl || currentUrl });
    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:site_name', content: 'Nexchat AI Agent' });

    // 4. Twitter Cards
    this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
    this.meta.updateTag({ name: 'twitter:title', content: data.ogTitle || titleText });
    this.meta.updateTag({ name: 'twitter:description', content: data.ogDescription || description });
    this.meta.updateTag({ name: 'twitter:image', content: ogImage });
    this.meta.updateTag({ name: 'twitter:site', content: twitterSite });

    // 5. Canonical Link
    this.setCanonicalURL(currentUrl);

    // 6. JSON-LD Schema Integration
    if (data.jsonLdSchema) {
      this.setJsonLd(data.jsonLdSchema);
    } else {
      // Set default Organization and WebSite schema if none provided
      this.setJsonLd(this.getDefaultOrganizationSchema());
    }
  }

  /**
   * Set or update canonical URL link tag
   */
  public setCanonicalURL(url?: string): void {
    const canURL: string =
      url ||
      (isPlatformBrowser(this.platformId) && this.dom?.location?.href
        ? this.dom.location.href
        : this.defaultBaseUrl) ||
      this.defaultBaseUrl;
    let link: HTMLLinkElement = this.dom.querySelector('link[rel="canonical"]');
    if (link) {
      link.setAttribute('href', canURL);
    } else {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canURL);
      this.dom.head.appendChild(link);
    }
  }

  /**
   * Dynamically inject or update JSON-LD schema script
   */
  public setJsonLd(schema: object | object[]): void {
    let script: HTMLScriptElement = this.dom.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = this.dom.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      this.dom.head.appendChild(script);
    }
    script.text = JSON.stringify(schema);
  }

  // --- Schema Generator Helpers ---

  public getDefaultOrganizationSchema(): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Nexchat AI Agent',
      'url': 'https://thenexchat.com',
      'logo': 'https://ai.myshopyglam.com/assets/icon/download%20(2)-Photoroom.png',
      'description': this.defaultDescription,
      'sameAs': [
        'https://facebook.com/nexchatai',
        'https://twitter.com/nexchatai',
        'https://instagram.com/nexchatai'
      ]
    };
  }

  public getWebSiteSchema(): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Nexchat AI Agent',
      'url': 'https://thenexchat.com',
      'potentialAction': {
        '@type': 'SearchAction',
        'target': 'https://thenexchat.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string'
      }
    };
  }

  public getFaqSchema(faqs: { question: string; answer: string }[]): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.map((faq) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      }))
    };
  }

  public getBlogPostingSchema(post: {
    headline: string;
    description: string;
    image: string;
    datePublished: string;
    dateModified?: string;
    authorName?: string;
    url?: string;
  }): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      'headline': post.headline,
      'description': post.description,
      'image': [post.image],
      'datePublished': post.datePublished,
      'dateModified': post.dateModified || post.datePublished,
      'author': {
        '@type': 'Person',
        'name': post.authorName || 'Nexchat Team'
      },
      'publisher': this.getDefaultOrganizationSchema(),
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': post.url || 'https://thenexchat.com/blog'
      }
    };
  }

  // --- Legacy Compatibility Methods ---

  public updateTitle(title: string): void {
    this.updateSeo({ title });
  }

  public updateTitleProduct(title: string): void {
    this.meta.updateTag({ property: 'og:title', content: title });
  }

  public updateDescription(desc: string): void {
    this.meta.updateTag({ name: 'description', content: desc });
    this.meta.updateTag({ property: 'og:description', content: desc });
  }

  public updateOgUrl(url: string): void {
    this.meta.updateTag({ property: 'og:url', content: url });
    this.setCanonicalURL(url);
  }

  public updateOgImage(img: string): void {
    this.meta.updateTag({ property: 'og:image', content: img });
  }

  public updateOgVideo(vid: string): void {
    this.meta.updateTag({ property: 'og:video', content: vid });
  }
}
